// Captures ⌘K (Cmd+K on macOS, Ctrl+K on Linux/Windows) and forwards it as a
// "toggle" event to the AdminPalette LiveComponent. Also handles keyboard
// navigation while the palette is open (↑ / ↓ / Enter / Esc).
//
// Lives at the window level so the shortcut works on any admin page. Mirrors
// the gating logic of `KeyboardShortcuts.js` — if the user is typing in an
// input outside the palette, only ⌘K and Esc fire; everything else passes
// through.
//
// LocalStorage recents:
//
// - Key: "tvlabs.cmdk.recents"
// - Value: JSON array of { serial, ts }, most recent first, max 10.
// - We seed the LiveComponent with recent serials on open, and record a
//   selection just before navigating away.
//
// The pure helpers (isTypingInExternalInput / readRecents / writeRecent)
// live in AdminPaletteHotkey.utils.js so they can be unit-tested without a
// LiveView/Phoenix harness.

import {
  isTypingInExternalInput,
  markAdminPaletteReady,
  readRecents,
  takePendingAdminPaletteToggle,
  writeRecent,
} from "./AdminPaletteHotkey.utils";

export const AdminPaletteHotkey = {
  mounted() {
    this._open = this.el.dataset.open === "true";

    this._onKeydown = (e) => {
      // Toggle: ⌘K / Ctrl+K. Works even when typing in another input.
      const isModK =
        (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "k" || e.key === "K");

      if (isModK) {
        e.preventDefault();
        e.stopPropagation();
        this.pushEventTo(this.el, "toggle", {});
        return;
      }

      if (!this._open) {
        return;
      }

      // While the palette is open, all nav keys are handled here so the
      // server sees clean events instead of a generic keydown.
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this.pushEventTo(this.el, "close", {});
          break;

        case "ArrowDown":
          e.preventDefault();
          this.pushEventTo(this.el, "cursor_down", {});
          break;

        case "ArrowUp":
          e.preventDefault();
          this.pushEventTo(this.el, "cursor_up", {});
          break;

        case "Enter":
          // The input form's phx-submit handles this when the input has
          // focus. If somehow another element inside the palette has focus
          // (shouldn't), this is a safety net.
          if (!isTypingInExternalInput(this.el)) {
            const focused = document.activeElement;
            if (focused && focused.matches("[data-cmdk-input]")) {
              return; // let the form submit
            }
          }
          e.preventDefault();
          this.pushEventTo(this.el, "select_current", {});
          break;

        default:
          break;
      }
    };

    // Record the serial we're about to navigate to, before LiveView swaps
    // pages. capture: true to beat phx-click's own handling.
    this._onClick = (e) => {
      const row = e.target.closest("[data-cmdk-row]");
      if (!row) return;
      const serial = row.dataset.serial;
      if (serial) writeRecent(serial);
    };

    // For keyboard "Enter on current row" — the form's submit event fires
    // before LiveView navigates away, so we snapshot the selected serial here.
    this._onSubmit = () => {
      if (!this._open) return;
      const selected = this.el.querySelector('[data-cmdk-row][aria-selected="true"]');
      if (selected && selected.dataset.serial) {
        writeRecent(selected.dataset.serial);
      }
    };

    // The sidebar's "Jump to…" button dispatches this, so it can open the
    // palette without knowing where the LiveComponent lives.
    this._onToggleEvent = () => this.pushEventTo(this.el, "toggle", {});

    // Claim the shortcut before adding our own listener. mounted() runs
    // synchronously, so a key cannot land between these lines. A press that
    // arrived earlier is replayed once the hook can push the toggle.
    markAdminPaletteReady(true);
    const pending = takePendingAdminPaletteToggle();

    window.addEventListener("keydown", this._onKeydown, true);
    window.addEventListener("admin-palette:toggle", this._onToggleEvent);
    this.el.addEventListener("click", this._onClick, true);
    this.el.addEventListener("submit", this._onSubmit, true);

    if (pending) this.pushEventTo(this.el, "toggle", {});
  },

  updated() {
    const wasOpen = this._open;
    this._open = this.el.dataset.open === "true";

    // Just opened? Seed recents from LocalStorage so the empty state isn't
    // empty for return visitors.
    if (!wasOpen && this._open) {
      const recents = readRecents();
      if (recents.length > 0) {
        this.pushEventTo(this.el, "seed_recents", {
          serial_numbers: recents,
        });
      }
    }
  },

  destroyed() {
    markAdminPaletteReady(false);
    window.removeEventListener("keydown", this._onKeydown, true);
    window.removeEventListener("admin-palette:toggle", this._onToggleEvent);
    this.el.removeEventListener("click", this._onClick, true);
    this.el.removeEventListener("submit", this._onSubmit, true);
  },
};
